/* eslint-disable camelcase */
import React, { useEffect, useState } from "react";
import {
    JobCard, JobCardProps, JobSearchHit, JobSearchData, JobSearchLocation
} from "@themuse/design-system";
import {
    triggerSnowplowClickEvent,
    SnowplowClickEventProps,
    SnowplowTileContentProps
} from "@themuse/snowplow-js-client";

import { useSearchState } from "hooks/SearchContext";
import { formatDate } from "utils/strings";
import { genJobTrackingProps, museTrack, TrackedElement } from "utils/tracking";
import { isBrowser } from "utils/browser";
import { externalJobApply } from "utils/externalJobApply";

interface GaWindow extends Window {
    ga: Function
}
declare const window: GaWindow;

interface JobTileProps {
    job: JobSearchHit;
    index: number;
    total: number;
    inputDMA: string;
    userLocationDma: string;
    showLogo?: boolean;
    viewed?: boolean;
    viewedCallback?: () => void;
}

interface GetJobTileLocationProps {
    inputDma: string;
    locationType: string;
    locations: JobSearchLocation[];
    sessionDma: string;
}

/**
 * Attempt to pull the closest matching location out of the job.
 */
const getJobTileLocation = (props: GetJobTileLocationProps): string[] => {
    const {
        inputDma,
        locationType,
        locations,
        sessionDma
    } = props;
    const isRemote: boolean = locationType === "virtual";
    // We always prefer the dropdown if it was used.
    const userLocation: string = inputDma || sessionDma;
    // We Only care about the actual location address.
    const locationStrings: string[] = locations.map((location) => location.address);
    // This is the fully matched location (city and state).
    const matchedLocation: string = locationStrings.find((loc) => loc === userLocation);
    // We may need to match only on the last segment of the location.
    const getState = (txt: string) => {
        if (typeof txt === "string") {
            return txt.split(",")[1]?.trim()?.toLowerCase();
        }
        return null;
    };
    // First location matched by region.
    const matchedRegion: string = locationStrings.find((address) => getState(address) === getState(userLocation));
    // First location in the list (fall back)
    const firstLocation: string = locationStrings[0];
    // The location to display
    const displayLocation: string = matchedLocation || matchedRegion || firstLocation || "";
    // If we have more than 2 locations pluralize the string.
    const locationWord: string = ` location${locationStrings.length > 2 ? "s" : ""}`;
    // If we have more than 1 location we add a plus sign and the number plus the word location(s)
    const extraLocations: string = locationStrings.length > 1 ? ` + ${locationStrings.length - 1}${locationWord}` : "";
    // If we are remote and there is a location we will separate them with a slash.
    const separator: string = displayLocation ? " / " : "";
    // Build the remote string.
    const remoteString: string = isRemote ? `${separator}Remote` : "";

    // all together now...
    return [`${displayLocation}${remoteString}${extraLocations}`];
};

/**
 * Generate the job apply link.
 */
const getJobTileApplyUrl = (job: JobSearchData) => {
    const {
        apply_type,
        company: urlCompany,
        legacy_id,
        short_title
    } = job;
    const { short_name } = urlCompany;
    return apply_type === "external"
        ? `/job/redirect/${legacy_id}`
        : `/jobs/${short_name}/${short_title}#apply-inline-form`;
};

/**
 * Generates the tracking props for a card.
 * Returns an object.
 */
const getTrackingProps = (
    job: JobSearchHit, viewJobHref: string, total: number, index: number, searchId: string
): Object => ({
    ...genJobTrackingProps(job),
    link_href: viewJobHref,
    total_results_on_page: total,
    placement: "search_results",
    order_num: index + 1,
    sp_tile_order: index + 1,
    sp_tile_search_id: searchId,
    sp_impression_search_id: searchId,
});

interface IHandleCardClick {
    viewJobHref: string;
    trackingProps: Object;
    cb: (eventName: string, eventProps: Object) => void;
}
interface IHandleCardRightClick {
    trackingProps: Object;
    cb: (eventName: string, eventProps: Object) => void;
}

/**
 * Generates a callback for the card click.
 * Returns a function.
 */
const generateHandleCardClick = (
    props: IHandleCardClick,
    snowplowEventProps: SnowplowClickEventProps & SnowplowTileContentProps
) => (event: React.SyntheticEvent<HTMLElement, Event>) => {
    const { viewJobHref, trackingProps, cb } = props;
    // Only fire on clicking non anchor elements
    if (!(event.target instanceof HTMLAnchorElement)) {
        window.open(viewJobHref, "_blank");
        cb("Job Tile Clicked", {
            ...trackingProps,
            click_type: "view_job_tile"
        });

        triggerSnowplowClickEvent(snowplowEventProps);
    }
};

/**
 * Generates a callback for the car right click.
 * Returns a function.
 */
const generateHandleCardRightClick = (props: IHandleCardRightClick) => (
    event: React.SyntheticEvent<HTMLElement, Event>
) => {
    const { trackingProps, cb } = props;
    // Only fire on clicking non anchor elements
    if (!(event.target instanceof HTMLAnchorElement)) {
        cb("Job Tile Clicked", {
            ...trackingProps,
            click_type: "view_job_tile"
        });
    }
};

interface IHandleCompanyNameClick {
    trackingProps: Object;
    cb: (eventName: string, eventProps: Object) => void;
}

/**
 * Generates a callback for the company name click.
 * Returns a function.
 */
const generateHandleCompanyNameClick = (props: IHandleCompanyNameClick) => () => {
    const { cb, trackingProps } = props;
    cb("Job Tile Clicked", {
        ...trackingProps,
        click_type: "company_name_profile"
    });
};

interface IHandleViewJobClick {
    trackingProps: Object,
    cb: (eventName: string, eventProps: Object) => void
}

/**
 * Generates a callback for the view job click.
 * Returns a function.
 */
const generateHandleViewJobClick = (
    props: IHandleViewJobClick,
    snowplowEventProps: SnowplowClickEventProps & SnowplowTileContentProps
) => {
    const { cb, trackingProps } = props;
    return () => {
        cb("Job Tile Clicked", { ...trackingProps, click_type: "view_job_button" });
        triggerSnowplowClickEvent(snowplowEventProps);
    };
};

interface IgenerateHandleApplyNowClick {
    trackingProps: {
        company_id?: number;
        company_name?: string;
        company_short_name?: string;
        job_id?: number;
        job_level?: string[];
        job_locations?: string[];
        job_name?: string;
        job_short_name?: string;
        job_type?: string;
    },
    apply_type: string,
    total: number,
    cb: (eventName: string, eventProps: Object) => void
}

/**
 * Generates a callback for the apply now click.
 * Returns a function.
 */
const generateHandleApplyNowClick = (
    props: IgenerateHandleApplyNowClick,
    snowplowEventProps: SnowplowClickEventProps & SnowplowTileContentProps
) => {
    const {
        trackingProps,
        apply_type,
        total,
        cb
    } = props;
    return () => {
        cb("Job Tile Clicked", {
            ...trackingProps,
            click_type: "apply_now_button"
        });
        cb("Job Apply Clicked", {
            ...trackingProps,
            job_type: apply_type,
            placement_position: "search results",
            total_results_on_page: total,
            click_type: "tile"
        });
        externalJobApply(trackingProps.company_id.toString(), trackingProps.job_id);
        if (isBrowser() && typeof window.ga !== "undefined") {
            window.ga("send", {
                hitType: "event",
                eventCategory: "ExternalJob",
                eventAction: "job_tile_apply",
                nonInteraction: true,
            });
        }
        triggerSnowplowClickEvent(snowplowEventProps);
    };
};

/**
 * Returns a fully formed and trackable job tile
 */
const JobTile = (props: JobTileProps) => {
    const { searchId } = useSearchState();

    const {
        job,
        index,
        inputDMA,
        userLocationDma,
        total,
        viewed = false,
        showLogo = false,
        viewedCallback
    } = props;
    const {
        company: searchResultsCompany,
        apply_type,
        id,
        snowplow_id,
        legacy_id,
        locations,
        short_title,
        title,
        posted_at,
        remote_work_location_type
    } = job?.hit ?? {};

    const applyNowHref = getJobTileApplyUrl(job.hit);
    const companyHref = `/profiles/${searchResultsCompany.short_name}`;
    const viewJobHref = `/jobs/${searchResultsCompany.short_name}/${short_title}`;

    // always deal with dates on the client.
    const [postedDate, setPostedDate] = useState("");
    useEffect(() => {
        setPostedDate(formatDate(new Date(posted_at * 1000)));
    });

    const trackingProps = getTrackingProps(job, viewJobHref, total, index, searchId);

    const tileLocation = getJobTileLocation({
        inputDma: inputDMA,
        locationType: remote_work_location_type,
        locations,
        sessionDma: userLocationDma
    });

    const snowplowTileContentProps: SnowplowTileContentProps = {
        sp_tile_type: "job",
        sp_tile_company_id: searchResultsCompany.legacy_id.toString(),
        sp_tile_entity_id: legacy_id.toString(),
        sp_tile_entity_uuid: snowplow_id || id,
        sp_tile_location: null,
        sp_tile_order: index + 1
    };

    const handleCardClick = generateHandleCardClick(
        { viewJobHref, trackingProps, cb: museTrack },
        {
            sp_click_component: "tile",
            sp_click_component_tile_type: "job",
            sp_click_position: "main",
            sp_click_target_page_section: "job_detail",
            sp_click_search_id: searchId,
            ...snowplowTileContentProps
        }
    );
    const handleCardRightClick = generateHandleCardRightClick({ trackingProps, cb: museTrack });
    const handleCompanyNameClick = generateHandleCompanyNameClick({ trackingProps, cb: museTrack });
    const handleViewJobClick = generateHandleViewJobClick(
        { trackingProps, cb: museTrack },
        {
            sp_click_component: "button",
            sp_click_component_tile_type: "job",
            sp_click_button_type: "job_view",
            sp_click_position: "main",
            sp_click_target_page_section: "job_detail",
            sp_click_search_id: searchId,
            ...snowplowTileContentProps
        }
    );
    // Apply on company site
    const handleApplyNowClick = generateHandleApplyNowClick(
        {
            trackingProps,
            apply_type,
            total,
            cb: museTrack
        },
        {
            sp_click_component: "button",
            sp_click_component_tile_type: "job",
            sp_click_button_type: "apply",
            sp_click_position: "main",
            sp_click_target_page_section: "off_site",
            sp_click_search_id: searchId,
            ...snowplowTileContentProps
        }
    );

    const jobCardProps: JobCardProps = {
        showLogo,
        companyName: searchResultsCompany.name,
        companyLogo: searchResultsCompany.logo,
        jobName: title,
        locations: tileLocation,
        companyHref,
        applyNowHref,
        postedDate,
        viewJobHref,
        onApplyNowClick: handleApplyNowClick,
        onViewJobClick: handleViewJobClick,
        onCompanyNameClick: handleCompanyNameClick,
        onCardClick: handleCardClick,
        onViewJobRightClick: handleViewJobClick,
        onApplyNowRightClick: handleApplyNowClick,
        onCompanyNameRightClick: handleCompanyNameClick,
        onCardRightClick: handleCardRightClick,
        isLazy: index > 1
    };

    return (
        <TrackedElement
            key={`tracked-${id}`}
            eventName="Job Tile Viewed"
            eventProps={trackingProps}
            enabled={!viewed}
            callback={viewedCallback}
        >
            <JobCard key={`card-${id}`} {...jobCardProps} />
        </TrackedElement>
    );
};

JobTile.displayName = "JobTile";
export {
    JobTile,
    generateHandleApplyNowClick,
    generateHandleCardClick,
    generateHandleCompanyNameClick,
    generateHandleViewJobClick,
    getJobTileLocation,
    getJobTileApplyUrl
};

export type {
    GetJobTileLocationProps,
    JobTileProps
};
